import { createUseStyles } from 'react-jss';
import { BackgroundColors } from '@picsart/design-system/foundation/backgroundColors';
import { TextColors } from '@picsart/design-system/foundation/textColors';
import { PrimaryColors } from '@picsart/design-system/foundation/primaryColors';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { isNativeMobile } from '@/utils/platform';

export const useAppContentStyles = createUseStyles(
  {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      backgroundColor: BackgroundColors.base,
      color: TextColors.base,
      overflow: 'hidden',
      position: 'relative',
      ...(isNativeMobile() && {
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
      }),
    },
    main: {
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: BackgroundColors.base,
    },
    projectLoading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
    },
    projectLoadingText: {
      color: TextColors.tint1,
    },
  },
  { name: 'appContent' }
);

export const useDesktopLayoutStyles = createUseStyles(
  {
    desktopPanels: {
      display: 'flex',
      flexDirection: 'row',
      flex: 1,
      overflow: 'hidden',
    },
    leftPanel: {
      display: 'flex',
      flexDirection: 'column',
      width: '30%',
      minWidth: 280,
      maxWidth: 380,
      overflow: 'hidden',
      backgroundColor: BackgroundColors.base,
      borderRadius: CornerRadius.r12,
      margin: [Spacings.s2, Spacings.s4, Spacings.s4, Spacings.s4],
      marginRight: Spacings.s2,
      border: `1px solid ${BackgroundColors.tint2}`,
    },
    rightPanel: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
      backgroundColor: BackgroundColors.tint1,
      borderRadius: CornerRadius.r12,
      margin: [Spacings.s2, Spacings.s4, Spacings.s4, Spacings.s4],
      marginLeft: Spacings.s2,
      border: `1px solid ${BackgroundColors.tint3}`,
    },
    panelContent: {
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: BackgroundColors.base,
    },
    tabContent: {
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: BackgroundColors.tint1,
    },
  },
  { name: 'desktopLayout' }
);

export const useMobileLayoutStyles = createUseStyles(
  {
    mobileContent: {
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: BackgroundColors.base,
      animation: '$fadeIn 0.25s ease-out',
    },
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  },
  { name: 'mobileLayout' }
);

export const useResultOverlayStyles = createUseStyles(
  {
    fullScreenOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 100,
      backgroundColor: BackgroundColors.base,
      display: 'flex',
      flexDirection: 'column',
      animation: '$slideUp 0.3s ease-out',
    },
    '@keyframes slideUp': {
      from: {
        opacity: 0,
        transform: 'translateY(30px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    overlayHeader: {
      padding: '12px 16px',
      borderBottom: `1px solid ${BackgroundColors.tint2}`,
      fontSize: 14,
      fontWeight: 600,
      color: TextColors.tint1,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      backgroundColor: BackgroundColors.base,
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: BackgroundColors.tint1,
      },
    },
    overlayContent: {
      flex: 1,
      overflow: 'hidden',
      backgroundColor: BackgroundColors.base,
    },
    backIcon: {
      width: 16,
      height: 16,
    },
  },
  { name: 'resultOverlay' }
);

export const useViewOptionsBarStyles = createUseStyles(
  {
    tabHeader: {
      display: 'flex',
      gap: 4,
      padding: '8px 16px',
      backgroundColor: BackgroundColors.tint1,
      borderTopLeftRadius: CornerRadius.r12,
      borderTopRightRadius: CornerRadius.r12,
    },
    tabButton: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 16px',
      fontSize: 14,
      fontWeight: 600,
      color: TextColors.tint1,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'color 0.15s ease',
      '&:hover': {
        color: TextColors.base,
      },
    },
    tabButtonActive: {
      color: PrimaryColors.base.default,
    },
    tabIcon: {
      width: 16,
      height: 16,
      flexShrink: 0,
    },
    viewOptions: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      marginLeft: 'auto',
      paddingLeft: 8,
    },
    viewOptionsDivider: {
      width: 1,
      height: 20,
      backgroundColor: BackgroundColors.tint2,
      margin: '0 4px',
    },
    viewOptionButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      padding: 0,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 6,
      cursor: 'pointer',
      color: TextColors.tint1,
      transition: 'color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        color: TextColors.base,
        backgroundColor: BackgroundColors.base,
      },
    },
    viewOptionButtonActive: {
      color: PrimaryColors.base.default,
      backgroundColor: BackgroundColors.base,
    },
    viewOptionIcon: {
      width: 16,
      height: 16,
    },
    viewSizeText: {
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1,
    },
  },
  { name: 'viewOptionsBar' }
);
